<template>
  <MapGeoJSONLayer :geojson="geojson" :layers="layers" />
</template>
<script lang="ts" setup>
import type { Database } from "../types/supabase";
import type { RawLayer } from "./RawLayer.vue";

const BATCH_SIZE = 1000;
const props = defineProps({
  runId: {
    type: String,
    required: true,
  },
});

type RunEvent = Omit<
  Database["public"]["Tables"]["run_events"]["Row"],
  "user_agent"
>;

async function* getRunEventBatches(
  runId: string
): AsyncGenerator<RunEvent[], void, undefined> {
  let hasMore = true;
  let lastDate: string | null = null;

  while (hasMore) {
    let currentQuery = supabase
      .from("run_events")
      .select("*")
      .eq("run_id", props.runId)
      .order("time", { ascending: true })
      .limit(BATCH_SIZE);

    if (lastDate) {
      currentQuery = currentQuery.gt("time", lastDate);
    }

    const { data, error, count } = await currentQuery;

    if (error) {
      throw error;
    }

    if (!data || data.length < BATCH_SIZE) {
      hasMore = false;
    }

    yield data;

    if (data && data.length > 0) {
      lastDate = data[data.length - 1].time;
    }
  }
}

const geojson: GeoJSON.FeatureCollection = reactive({
  type: "FeatureCollection",
  features: [],
});

const colors = ["red", "purple", "blue", "green", "gray"];

function getFeature(deviceId: string): GeoJSON.Feature {
  let feature = geojson.features.find(
    (f) => f.properties?.deviceId === deviceId
  );
  if (feature) return feature;

  feature = {
    type: "Feature",
    properties: { deviceId, color: colors[geojson.features.length] },
    geometry: {
      coordinates: [],
      type: "LineString",
    },
  };

  geojson.features.push(feature);
  return feature;
}

const supabase = useClient();

async function worker() {
  const id = props.runId;
  let result = undefined;

  const runEventGenerator = getRunEventBatches(id);

  for await (const batch of runEventGenerator) {
    if (props.runId !== id) return;
    for (const row of batch) {
      const feature = getFeature(row.device_id || "unknown");
      if (feature.geometry.type !== "LineString")
        throw new Error("unexpected geometry type");
      feature.geometry.coordinates!.push([Number(row.lng), Number(row.lat)]);
    }
  }
}

watch(() => props.runId, worker, { immediate: true });

const layers = [
  {
    type: "line",
    paint: {
      "line-color": ["get", "color"],
      "line-opacity": 0.8,
    },
  },
] as RawLayer[];
</script>
